import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import styles from "./ProjectSingleImages.module.css";

const apiKey = "AIzaSyAAgFvKs3Vo5nuo_bkIY1ePkNqNsSmB33g";
const mainFolderId = "1se1WvZ297MXYbBHjl8qxIpvrRgJCv35b";

const ProjectSingleImages = ({ children, categoryFilter }) => {
    const { slug } = useParams();

    const [images, setImages] = useState([]);
    const [allImages, setAllImages] = useState([]);
    const [displayedImages, setDisplayedImages] = useState([]); // For lazy loading

    /* 🔹 FILTER CONSTANTS */
    const [categories, setCategories] = useState([]);
    const [activeFilter, setActiveFilter] = useState("All");

    /* LIGHTBOX STATES */
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    const realName = slug ? slug.split("-").join(" ").toLowerCase() : "";

    const fetchCategories = async () => {
        const url = `https://www.googleapis.com/drive/v3/files?q='${mainFolderId}' in parents and mimeType='application/vnd.google-apps.folder'&fields=files(id,name)&key=${apiKey}`;
        const res = await axios.get(url);
        return res.data.files;
    };

    const findProjectFolder = async (folderList) => {
        const promises = folderList.map(async (cat) => {
            const url = `https://www.googleapis.com/drive/v3/files?q='${cat.id}' in parents and mimeType='application/vnd.google-apps.folder'&fields=files(id,name)&key=${apiKey}`;
            const res = await axios.get(url);
            const match = res.data.files.find(
                f => f.name.toLowerCase() === realName
            );
            return match ? match.id : null;
        });

        const results = await Promise.all(promises);
        return results.find(id => id !== null);
    };

    const loadFolderImages = async (folderId, setState = true) => {
        // FIXED: Added 'name' to fields
        const url = `https://www.googleapis.com/drive/v3/files?q='${folderId}' in parents and mimeType contains 'image/'&fields=files(id,name)&key=${apiKey}`;
        const res = await axios.get(url);

        const imgs = res.data.files.map(file => ({
            id: file.id,
            name: file.name, // Store name
            thumb: `https://drive.google.com/thumbnail?id=${file.id}&sz=w600`,
            full: `https://drive.google.com/thumbnail?id=${file.id}&sz=w2000`,
        }));

        if (setState) setImages(imgs);
        return imgs;
    };

    const loadProjectRoot = async (folderId) => {
        const url = `https://www.googleapis.com/drive/v3/files?q='${folderId}' in parents&fields=files(id,name,mimeType)&key=${apiKey}`;
        const res = await axios.get(url);

        let subFolders = [];
        let directImages = [];

        res.data.files.forEach(file => {
            if (file.mimeType === "application/vnd.google-apps.folder") {
                subFolders.push(file);
            } else if (file.mimeType.includes("image")) {
                directImages.push({
                    id: file.id,
                    name: file.name, // Store name
                    thumb: `https://drive.google.com/thumbnail?id=${file.id}&sz=w600`,
                    full: `https://drive.google.com/thumbnail?id=${file.id}&sz=w2000`
                });
            }
        });

        // Check for subfolders
        const folderPromises = subFolders.map(folder => loadFolderImages(folder.id, false));
        const results = await Promise.all(folderPromises);
        let folderImages = results.flat();

        // Combine direct images and folder images
        let allProjectImages = [...directImages, ...folderImages];

        if (allProjectImages.length > 0) {
            setAllImages(allProjectImages);
            setImages(allProjectImages);
        } else {
            setAllImages([]);
            setImages([]);
        }
    };

    /* 🔹 EXTRACT CATEGORIES FROM IMAGES */
    useEffect(() => {
        if (!allImages.length) return;

        console.log("ALL IMAGES LOADED:", allImages.map(img => img.name));

        const uniqueCategories = new Set();
        const displayMap = {}; // To store the "pretty" version (e.g. "Outerior") vs normalized key

        allImages.forEach((img) => {
            if (img.name && img.name.includes("__")) {
                const rawCat = img.name.split("__")[0];
                const normalizedCat = rawCat.toLowerCase();

                // Store normalized category
                uniqueCategories.add(normalizedCat);

                // Store a "pretty" display version (first letter uppercase)
                if (!displayMap[normalizedCat]) {
                    displayMap[normalizedCat] = rawCat.charAt(0).toUpperCase() + rawCat.slice(1).toLowerCase();
                }
            }
        });

        console.log("EXTRACTED CATEGORIES:", [...uniqueCategories]);

        if (uniqueCategories.size > 0) {
            // Store objects with { id: 'outerior', label: 'Outerior' }
            const catList = [...uniqueCategories].map(cat => ({
                id: cat, // normalized (lowercase)
                label: displayMap[cat] // Display name
            })).sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically

            setCategories(catList);
        }
    }, [allImages]);

    /* 🔹 INITIALIZE FILTER FROM SESSION STORAGE */
    useEffect(() => {
        const savedFilter = sessionStorage.getItem(`activeProjectFilter_${slug}`);
        if (savedFilter) {
            setActiveFilter(savedFilter);
        }
    }, [slug]);

    /* 🔹 HANDLE FILTER CLICK */
    const handleFilter = (catId) => {
        setActiveFilter(catId);
        sessionStorage.setItem(`activeProjectFilter_${slug}`, catId);

        console.log("FILTERING BY:", catId);

        if (catId === "All") {
            setImages(allImages);
        } else {
            const filtered = allImages.filter(img =>
                img.name && img.name.toLowerCase().includes(catId.toLowerCase() + "__")
            );
            console.log("FILTERED COUNT:", filtered.length);
            setImages(filtered);
        }
    };

    /* 🔹 APPLY FILTER ON LOAD */
    useEffect(() => {
        if (allImages.length === 0) return;

        const savedFilter = sessionStorage.getItem(`activeProjectFilter_${slug}`) || "All";

        // Ensure the active state matches what we are about to filter
        setActiveFilter(savedFilter);

        if (savedFilter === "All") {
            setImages(allImages);
        } else {
            const filtered = allImages.filter(img =>
                img.name && img.name.toLowerCase().includes(savedFilter.toLowerCase() + "__")
            );
            setImages(filtered);
        }
    }, [allImages, slug]);


    useEffect(() => {
        const init = async () => {
            if (!slug) return;
            const cats = await fetchCategories();
            const projectId = await findProjectFolder(cats);

            if (projectId) {
                await loadProjectRoot(projectId);
                // await fetchProjectText(projectId);
            } else {
                // setLoading(false);
            }
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    // Load first 5 images, then load rest after delay
    useEffect(() => {
        if (images.length > 0) {
            // 1. Immediately show the first 5 of the NEW filtered set
            setDisplayedImages(images.slice(0, 5));

            // 2. Clear any previous timeouts to prevent race conditions
            const timer = setTimeout(() => {
                setDisplayedImages(images);
            }, 300); // Reduced delay for better UX

            return () => clearTimeout(timer);
        } else {
            // If no images (like if a filter has 0 results), clear the display
            setDisplayedImages([]);
        }
    }, [images]); // This runs every time 'images' changes (on filter click)

    /* LIGHTBOX CONTROLS */
    const openLightbox = (index) => {
        setCurrentIndex(index);
        setLightboxOpen(true);
        // document.body.style.overflow = "hidden";
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        // document.body.style.overflow = "auto";
    };

    const prevImage = () =>
        setCurrentIndex((currentIndex - 1 + images.length) % images.length);

    const nextImage = () =>
        setCurrentIndex((currentIndex + 1) % images.length);


    return (
        <>
            {/* 🔹 COMBINED STICKY HEADER */}
            <div className={styles.stickyHeader}>
                {/* 1. Parent Category Filter (ProjectSingle) */}
                {categoryFilter}

                {/* 2. Local Sub-Category Filter (Images) */}
                {categories.length > 0 && (
                    <div className={styles.filterWrapper}>
                        <button
                            className={`${styles.filterBtn} ${activeFilter === "All" ? styles.active : ""}`}
                            onClick={() => handleFilter("All")}
                        >
                            All
                        </button>

                        {categories.map((cat, index) => (
                            <button
                                key={index}
                                className={`${styles.filterBtn} ${activeFilter === cat.id ? styles.active : ""}`}
                                onClick={() => handleFilter(cat.id)}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {children}

            {/* Masonry */}
            <div className={styles.masonry}>
                {displayedImages.map((img, index) => (
                    <div
                        key={img.id || index}
                        className={styles.item}
                        onClick={() => openLightbox(index)}
                    >
                        <img src={img.thumb} alt="" loading="lazy" />
                    </div>
                ))}
            </div>

            {/* Popup */}
            {lightboxOpen && (
                <div className={styles.popup}>
                    <span className={styles.close} onClick={closeLightbox}>×</span>

                    {/* LEFT → CLOSE */}
                    <div
                        className={styles.leftZone}
                        onClick={closeLightbox}
                    />

                    <div className={styles.lightboxFrame}>
                        <img
                            src={images[currentIndex].full}
                            className={styles.popupImg}
                            alt=""
                        />
                    </div>

                    {/* RIGHT → CLOSE */}
                    <div
                        className={styles.rightZone}
                        onClick={closeLightbox}
                    />

                    {/* ARROWS → NAVIGATION */}
                    <button
                        className={styles.prev}
                        onClick={prevImage}
                    >
                        ❮
                    </button>

                    <button
                        className={styles.next}
                        onClick={nextImage}
                    >
                        ❯
                    </button>
                </div>
            )}

        </>
    );
};

export default ProjectSingleImages;
